/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import FormBox from '../../common/FormBox/FormBox';
import { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { FaTrash } from 'react-icons/fa';
import { formCounterSlice } from '../../../redux/formCounter';
import { useDispatch, useSelector } from 'react-redux';
import api from 'api';
import ToggleButtonGroup from 'components/common/ToggleButtonGroup/ToggleButtonGroup';
import PartnerInfoSwitch from './PartnerInfoSwitch';
import ReactGA from 'react-ga4';
import AddEntryFooter from '../AddEntryFooter';
import * as S from '../Forms.styled';
import { useParams } from 'react-router-dom';
import { RootState } from 'redux/store';
import { useSpouseTableTarget } from 'hooks/useSpouseUserId';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { EARLIEST_EDUCATION_DATE } from '../../../data/constants/constants';
import { useTranslation } from 'react-i18next';
import { formatDateToYMD, isFullDateFormat, parseStoredDate } from 'utils/fullDateFormat';
import { startOfDay } from 'date-fns';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { chosenCountryName, countryNameOptions } from 'utils/countries';

interface Entry {
    current: boolean | undefined;
    duration: string;
    country: string | null;
    region: string | null;
    completed: boolean;
    type: string | null;
    start: Date | null;
    end: Date | null;
}

const initEntry: Entry = {
    current: undefined,
    duration: '',
    country: '',
    region: '',
    completed: false,
    type: '',
    start: null,
    end: null,
};

interface Msg {
    current: string;
    duration: string;
    country: string;
    region: string;
    completed: string;
    type: string;
    start: string;
    end: string;
}

const initMsg: Msg = {
    current: '',
    duration: '',
    country: '',
    region: '',
    completed: '',
    type: '',
    start: '',
    end: '',
};

export default function PartnerEducationForm() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [data, setData] = useState<Entry[]>([]);
    const [validationMsg, setValidationMsg] = useState<Msg[]>([]);
    const { setShowSpouse } = formCounterSlice.actions;
    const dispatch = useDispatch();
    const { codes } = useEnabledCountries();

    const [isSpouseInfo, setIsSpouseInfo] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [educationOptions, setEducationOptions] = useState([]);
    const [regionOptions, setRegionOptions] = useState({} as { [key: string]: string[] });
    // const [countryOptions, setCountryOptions] = useState<string[]>([]);

    useEffect(() => {
        getEducationOptions();
        // getCountryOptions();
        getRegionOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isSpouseInfo) {
            setIsLoading(false);
        }
    }, [isSpouseInfo]);

    const fetchOptions = async (
        fetchFunction: () => Promise<any>,
        setFunction: (data: any) => void
    ) => {
        try {
            const resp = await fetchFunction();
            if (resp?.status === 200 || resp?.status === 201) {
                if (resp?.data) {
                    setFunction(resp.data);
                }
            }
        } catch (err) {
            console.log(err);
        }
    };

    const getEducationOptions = () =>
        fetchOptions(api?.assessment_options.getEducationOptions, setEducationOptions);
    // const getCountryOptions = () =>
    //     fetchOptions(api?.assessment_options.getCountryOptions, setCountryOptions);
    const getRegionOptions = () =>
        fetchOptions(api?.assessment_options.getRegionOptions, setRegionOptions);

    const withDestinationCountry = (rows: Entry[]) => {
        const sole = chosenCountryName(codes) ?? '';
        return rows.map((row) => ({ ...row, country: row.country || sole }));
    };

    const loadData = (data: Entry[]) => {
        if (data && data.length | Object.keys(data).length) {
            const tmp = new Array(data.length).fill(initMsg);
            setValidationMsg(tmp);
            setData(withDestinationCountry(data));
        }
        if (data == null || data) {
            setIsSpouseInfo(true);
        } else {
            setIsSpouseInfo(false);
        }
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname + '/' + tableName}`,
            title: 'PartnerEducationForm',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const params = useParams();
    const candidate_id = Number(params.id);
    const validCandidateId =
        Number.isFinite(candidate_id) && candidate_id > 0 ? candidate_id : undefined;
    const { tableName, targetUserId, isReady } = useSpouseTableTarget(
        'EducationNew',
        'SpouseEducation',
        validCandidateId
    );
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        if (!isAuthenticated || !isReady) {
            return;
        }
        setIsLoading(true);
        setIsSpouseInfo(false);
        setData([]);
        api.tableInput.fetchCandidateInput(loadData, tableName, targetUserId ?? validCandidateId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validCandidateId, isAuthenticated, isReady, tableName, targetUserId]);

    // FIELD VALIDATION
    const validate = () => {
        let validated = true;
        const temp = [...validationMsg];
        for (let i = 0; i < data.length; i++) {
            for (const key of Object.keys(initEntry)) {
                const value = data[i][key as keyof Entry];
                let msg = '';
                if (
                    key === 'region' &&
                    !['Australia', 'Canada', 'United Kingdom'].includes(data[i].country as string)
                )
                    continue;
                // End required only when completed and not current (matches v2 stepper).
                if (key === 'end' && (!data[i].completed || data[i].current)) continue;
                else if (value === '' || value === undefined || value === null) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp[i] = { ...temp[i], [key]: msg };
            }
        }
        setValidationMsg(temp);
        return validated;
    };

    const validateSingleField = (idx: number, field: string, value: string | boolean | null) => {
        const temp = [...validationMsg];
        let msg = '';
        if (!value) {
            msg = t('requiredField', { lng: language });
        }
        temp[idx] = { ...temp[idx], [field]: msg };
        setValidationMsg(temp);
    };

    const handleUpdate = (idx: number, field: string, value: string | boolean | null) => {
        validateSingleField(idx, field, value);
        const temp = [...data];
        if (field === 'country') {
            // init region
            temp[idx] = { ...temp[idx], region: '' };
        }
        temp[idx] = { ...temp[idx], [field]: value };
        setData(temp);
    };

    const handleAddEntry = () => {
        if (data.length > 10) return;
        const newEntry: Entry = { ...initEntry, country: chosenCountryName(codes) ?? '' };

        const temp: Entry[] = [...data, newEntry];
        setData(temp);

        const tempMsg: Msg[] = [...validationMsg, initMsg];
        setValidationMsg(tempMsg);
    };

    const handleDeleteEntry = (idx: number) => {
        const temp: Entry[] = [...data];
        temp.splice(idx, 1);
        setData(temp);
        const tempMsg: Msg[] = [...validationMsg];
        tempMsg.splice(idx, 1);
        setValidationMsg(tempMsg);
    };

    function handleSwitchPartnerMe() {
        if (validate()) {
            const writeId = targetUserId ?? validCandidateId;
            if (writeId) {
                api.tableInput.postSales(data, tableName, writeId);
            } else {
                api.tableInput.post(data, tableName);
            }
            dispatch(setShowSpouse());
        }
    }

    const handleDateUpdate = (idx: number, field: string, newDate: Date | null) => {
        if (newDate === null) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                [field]: t('requiredField', { lng: language }),
            };
            setValidationMsg(temp);
            const tmp = [...data];
            tmp[idx] = { ...tmp[idx], [field]: '' };
            setData(tmp);
            return;
        }
        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            return;
        }

        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            // Incomplete year while typing — keep month visible
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            const tmp = [...data];
            tmp[idx] = { ...tmp[idx], [field]: extractDateFormat };
            setData(tmp);
            return;
        }

        const temp = [...validationMsg];
        temp[idx] = { ...temp[idx], [field]: '' };
        setValidationMsg(temp);
        const tmp = [...data];
        tmp[idx] = { ...tmp[idx], [field]: extractDateFormat };
        if (field === 'start') {
            tmp[idx] = { ...tmp[idx], end: null };
        }
        setData(tmp);
    };

    const checkEndDateBeforeStart = (
        endDate: Date | null,
        startDate: Date | null | undefined,
        idx: number
    ) => {
        const normalizedStartDate = startDate ? startOfDay(new Date(startDate)) : undefined;
        const normalizedEndDate = endDate ? startOfDay(endDate) : undefined;

        if (normalizedEndDate && normalizedStartDate && normalizedEndDate < normalizedStartDate) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                end: t('endDateBeforeStartDate', { lng: language }),
            };
            setValidationMsg(temp);
            return true;
        }

        return false;
    };

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox
                    validation={validate}
                    data={[data]}
                    tableNames={[tableName]}
                    targetUserId={targetUserId}
                >
                    <S.TitleContainer>
                        <S.Title>{t('partnerEducation', { lng: language })}</S.Title>
                        <PartnerInfoSwitch partnerActive={true} onSwitch={handleSwitchPartnerMe} />
                    </S.TitleContainer>
                    {data.map((_, i) => {
                        return (
                            <S.EntryCard key={i}>
                                <div>
                                    <S.SubTitle>
                                        <>{`${t('education', { lng: language })} ${i + 1}`}</>
                                        <S.DeleteButton>
                                            <button onClick={() => handleDeleteEntry(i)}>
                                                <FaTrash />
                                            </button>
                                        </S.DeleteButton>
                                    </S.SubTitle>
                                </div>
                                <S.FlexColumn>
                                    <S.FlexColumn>
                                        <S.GridSection>
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    disablePortal
                                                    id="country"
                                                    value={data[i].country}
                                                    options={countryNameOptions(
                                                        codes,
                                                        data[i].country
                                                    )}
                                                    onChange={(_, value: string | null) => {
                                                        handleUpdate(i, 'country', value);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('country', {
                                                                lng: language,
                                                            })}`}
                                                            error={Boolean(
                                                                validationMsg[i].country
                                                            )}
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].country}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            {data[i]?.country && (
                                                <S.FlexColumn>
                                                    <Autocomplete
                                                        disablePortal
                                                        id="region"
                                                        value={data[i].region}
                                                        options={
                                                            regionOptions[
                                                                data[i].country as string
                                                            ] || []
                                                        }
                                                        onChange={(_, value: string | null) =>
                                                            handleUpdate(i, 'region', value)
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`*${t('region', {
                                                                    lng: language,
                                                                })}`}
                                                                error={Boolean(
                                                                    validationMsg[i].region
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                    <S.ValidationMsg>
                                                        {validationMsg[i].region}
                                                    </S.ValidationMsg>
                                                </S.FlexColumn>
                                            )}
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    id="test_type"
                                                    value={String(data[i].duration)}
                                                    options={['', '1', '2', '3', '4', '5', '6']}
                                                    onChange={(_, v: string | null) => {
                                                        handleUpdate(i, 'duration', v);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('programDurationInYears', {
                                                                lng: language,
                                                            })}`}
                                                            error={Boolean(
                                                                validationMsg[i].duration
                                                            )}
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].duration}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    disablePortal
                                                    id="Type"
                                                    value={data[i].type}
                                                    options={educationOptions}
                                                    onChange={(_, v: string | null) =>
                                                        handleUpdate(i, 'type', v)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('type', {
                                                                lng: language,
                                                            })}`}
                                                            error={Boolean(validationMsg[i].type)}
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].type}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>

                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DesktopDatePicker
                                                    views={['year', 'month']}
                                                    minDate={new Date(EARLIEST_EDUCATION_DATE)}
                                                    maxDate={new Date()}
                                                    label={`*${t('startDate', { lng: language })}`}
                                                    format="MM/yyyy"
                                                    openTo="year"
                                                    value={parseStoredDate(data[i].start)}
                                                    onChange={(date: Date | null) => {
                                                        handleDateUpdate(i, 'start', date);
                                                    }}
                                                    slotProps={{
                                                        field: { clearable: true },
                                                        textField: {
                                                            error: validationMsg[i].start !== '',
                                                            helperText: validationMsg[i].start,
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>

                                            <ToggleButtonGroup
                                                options={[
                                                    t('yes', { lng: language }),
                                                    t('no', { lng: language }),
                                                ]}
                                                name="current"
                                                label={t('ongoing', { lng: language })}
                                                value={
                                                    data[i].current === undefined ||
                                                    data[i].current === null
                                                        ? undefined
                                                        : data[i].current
                                                          ? t('yes', { lng: language })
                                                          : t('no', { lng: language })
                                                }
                                                onChange={(arg1: string, arg2: string) =>
                                                    handleUpdate(
                                                        i,
                                                        arg1,
                                                        arg2 === t('yes', { lng: language })
                                                    )
                                                }
                                                error={validationMsg[i].current}
                                            />

                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DesktopDatePicker
                                                    views={['year', 'month']}
                                                    minDate={
                                                        parseStoredDate(data[i].start) ??
                                                        new Date(EARLIEST_EDUCATION_DATE)
                                                    }
                                                    maxDate={new Date()}
                                                    label={`*${t('endDate', { lng: language })}`}
                                                    disabled={data[i].current}
                                                    openTo="year"
                                                    format="MM/yyyy"
                                                    value={
                                                        data[i].current
                                                            ? null
                                                            : parseStoredDate(data[i].end)
                                                    }
                                                    onChange={(date: Date | null) => {
                                                        // Commit typed value first so the field stays in sync
                                                        // (early return left partial years e.g. 0201 on screen).
                                                        handleDateUpdate(i, 'end', date);
                                                        if (date && isFullDateFormat(date)) {
                                                            checkEndDateBeforeStart(
                                                                date,
                                                                parseStoredDate(data[i].start),
                                                                i
                                                            );
                                                        }
                                                    }}
                                                    slotProps={{
                                                        field: { clearable: true },
                                                        textField: {
                                                            error: validationMsg[i].end !== '',
                                                            helperText: validationMsg[i].end,
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>

                                            <ToggleButtonGroup
                                                options={[
                                                    t('yes', { lng: language }),
                                                    t('no', { lng: language }),
                                                ]}
                                                name="completed"
                                                label={t('completed', { lng: language })}
                                                value={
                                                    data[i].completed === undefined
                                                        ? undefined
                                                        : data[i].completed
                                                          ? t('yes', { lng: language })
                                                          : t('no', { lng: language })
                                                }
                                                onChange={(arg1: any, arg2: any) =>
                                                    handleUpdate(
                                                        i,
                                                        arg1,
                                                        arg2 === t('yes', { lng: language })
                                                    )
                                                }
                                                error={validationMsg[i].completed}
                                            />
                                        </S.GridSection>
                                    </S.FlexColumn>
                                </S.FlexColumn>
                            </S.EntryCard>
                        );
                    })}
                    <AddEntryFooter
                        title={t('addEducation', { lng: language })}
                        addLabel={t('addEducation', { lng: language })}
                        addAnotherLabel={t('addAnotherEducation', { lng: language })}
                        hasEntries={data.length > 0}
                        onClick={handleAddEntry}
                    />
                </FormBox>
            )}
        </>
    );
}
